
const libPictView = require('pict-view');

/**
 * Create-projection wizard view — #/Datalake/New.
 *
 * A 5-step accordion (Source → Columns → Name & schedule → Review → Create) over the
 * DatalakeProjectionEditorProvider. Thin view: derives + repaints declaratively; all behaviour + the
 * generated POST body live in the provider + the pure Datalake-Projection-Builder.
 */
class DatalakeProjectionEditor extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get provider()
	{
		return this.pict.providers.DatalakeProjectionEditorProvider;
	}

	deriveRenderState()
	{
		if (this.provider)
		{
			this.provider.deriveRenderState();
		}
	}

	onBeforeRender(pRenderable)
	{
		this.deriveRenderState();
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this.pict.CSSMap.injectCSS();
		// Mount the searchable lake-source picker (present only in Data-lake source mode; no-ops otherwise).
		if (this.provider) { this.provider.mountLakeSourcePicker(); }
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = DatalakeProjectionEditor;

module.exports.default_configuration =
{
	ViewIdentifier: 'DatalakeProjectionEditor',
	DefaultRenderable: 'DatalakeProjectionEditorWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,

	CSS: /*css*/`
		.dpe-wrap { padding: 1.25rem 1.5rem 2rem; max-width: 980px; }
		.dpe-step { border: 1px solid var(--bulma-border, #e6e9ef); border-radius: 10px; margin-bottom: 0.8rem; overflow: hidden; }
		.dpe-step-head { display: flex; align-items: center; gap: 0.7rem; padding: 0.7rem 1rem; background: var(--bulma-scheme-main-bis, #f8fafc); cursor: pointer; }
		.dpe-step-num { flex: 0 0 auto; width: 1.6rem; height: 1.6rem; border-radius: 50%; background: var(--bulma-border, #dfe3ea); color: var(--bulma-text, #4a4a4a); display: inline-flex; align-items: center; justify-content: center; font-size: 0.8rem; font-weight: 700; }
		.dpe-step-title { font-weight: 600; flex: 1 1 auto; }
		.dpe-step-body { padding: 1rem; }
		.dpe-fields { }
		.dpe-fields td, .dpe-fields th { vertical-align: middle; }
		.dpe-fields .input { max-width: 13rem; }
		.dpe-review { background: #1f2733; color: #e6edf3; border-radius: 8px; padding: 0.85rem 1rem; font-size: 0.8rem; overflow: auto; max-height: 26rem; }
		.dpe-target { font-family: monospace; background: var(--bulma-scheme-main-bis, #f2f5f9); border-radius: 6px; padding: 0.35rem 0.6rem; display: inline-block; }
		.dpe-sched { display: flex; align-items: center; flex-wrap: wrap; gap: 0.8rem; margin-top: 0.5rem; }
		.dpe-sched .input { max-width: 12rem; }
		.dpe-spinner { display: inline-block; width: 1.2rem; height: 1.2rem; border: 3px solid var(--bulma-border, #dbdbdb); border-top-color: var(--bulma-link, #156dd1); border-radius: 50%; animation: dpe-spin 0.8s linear infinite; vertical-align: middle; }
		@keyframes dpe-spin { to { transform: rotate(360deg); } }
	`,

	Templates:
	[
		{
			Hash: 'DPE-Wrap',
			Template: /*html*/`
				<div class="dpe-wrap">
					<div class="dpe-back mb-3"><a class="button is-small is-light" onclick="window.location.hash='#/Datalake'"><span class="icon is-small"><i class="fas fa-arrow-left"></i></span><span>Data Lake</span></a></div>
					<h1 class="title is-4"><span class="icon"><i class="fas fa-plus"></i></span> New Projection</h1>
					<p class="subtitle is-6 has-text-grey">Create a materialized projection from the Headlight data model — pick a source, project the columns you want, and (optionally) schedule it to refresh.</p>

					{~TS:DPE-CloneBanner:AppData.DatalakeProjectionEditor.CloneBannerSlot~}

					<!-- Step 1: Source -->
					<div class="dpe-step {~D:AppData.DatalakeProjectionEditor.Step1_CardClass~}">
						<div class="dpe-step-head" onclick="_Pict.providers.DatalakeProjectionEditorProvider.goToStep(1)">
							<span class="dpe-step-num">1</span><span class="dpe-step-title">Source</span><span class="tag {~D:AppData.DatalakeProjectionEditor.Step1_StatusClass~}">{~D:AppData.DatalakeProjectionEditor.Step1_StatusLabel~}</span>
						</div>
						<div class="dpe-step-body {~D:AppData.DatalakeProjectionEditor.Step1_BodyClass~}">
							<div class="field">
								<label class="label is-small">Where does the data come from?</label>
								<div class="control">
									<label class="radio mr-4"><input type="radio" name="dpe-src-type" {~D:AppData.DatalakeProjectionEditor.SourceTypeEntityChecked~} onchange="_Pict.providers.DatalakeProjectionEditorProvider.setSourceType('entity')" /> Headlight data model</label>
									<label class="radio"><input type="radio" name="dpe-src-type" {~D:AppData.DatalakeProjectionEditor.SourceTypeLakeChecked~} onchange="_Pict.providers.DatalakeProjectionEditorProvider.setSourceType('lake')" /> Data lake (project from a projection or imported clone)</label>
								</div>
							</div>
							{~TS:DPE-EntityPicker:AppData.DatalakeProjectionEditor.EntityPickerSlot~}
							{~TS:DPE-LakePicker:AppData.DatalakeProjectionEditor.LakePickerSlot~}
							<div class="field"><label class="label is-small">Filter (optional, FoxHound)</label>
								<div class="control"><input class="input is-small" type="text" placeholder="FBV~Status~EQ~Active~FBV~Deleted~EQ~0" value="{~D:AppData.DatalakeProjectionEditor.Filter~}" oninput="_Pict.providers.DatalakeProjectionEditorProvider.setFilter(this.value)" /></div>
								<p class="help">Restrict which source rows are projected. Leave blank for all.</p>
							</div>
							{~TS:DPE-TestCount:AppData.DatalakeProjectionEditor.TestCountSlot~}
							{~TS:DPE-TestResult:AppData.DatalakeProjectionEditor.TestResultSlot~}
							<div class="field mt-3"><p class="control"><button class="button is-link" onclick="_Pict.providers.DatalakeProjectionEditorProvider.continueFromSource()">Continue</button></p></div>
						</div>
					</div>

					<!-- Step 2: Columns -->
					<div class="dpe-step {~D:AppData.DatalakeProjectionEditor.Step2_CardClass~}">
						<div class="dpe-step-head" onclick="_Pict.providers.DatalakeProjectionEditorProvider.goToStep(2)">
							<span class="dpe-step-num">2</span><span class="dpe-step-title">Columns &amp; key</span><span class="tag {~D:AppData.DatalakeProjectionEditor.Step2_StatusClass~}">{~D:AppData.DatalakeProjectionEditor.Step2_StatusLabel~}</span>
						</div>
						<div class="dpe-step-body {~D:AppData.DatalakeProjectionEditor.Step2_BodyClass~}">
							{~TS:DPE-SchemaLoading:AppData.DatalakeProjectionEditor.SchemaLoadingSlot~}
							{~TS:DPE-SchemaError:AppData.DatalakeProjectionEditor.SchemaErrorSlot~}
							<div class="level is-mobile mb-2"><div class="level-left"><p class="is-size-7 has-text-grey">{~D:AppData.DatalakeProjectionEditor.IncludedCount~} column(s) selected</p></div><div class="level-right"><button class="button is-small is-white" onclick="_Pict.providers.DatalakeProjectionEditorProvider.toggleShowAll()">{~D:AppData.DatalakeProjectionEditor.ShowAllLabel~}</button></div></div>
							<div class="dpe-fields">
								<table class="table is-narrow is-fullwidth is-hoverable">
									<thead><tr><th>Use</th><th>Source field</th><th>Output column</th><th>Type</th></tr></thead>
									<tbody>{~TS:DPE-FieldRow:AppData.DatalakeProjectionEditor.FieldRows~}</tbody>
								</table>
							</div>
							{~TS:DPE-FormDataSection:AppData.DatalakeProjectionEditor.FormDataSlot~}
							<div class="field mt-2"><label class="label is-small">Row key (GUID template)</label>
								<div class="control"><input class="input is-small" type="text" value="{~D:AppData.DatalakeProjectionEditor.GUIDTemplate~}" oninput="_Pict.providers.DatalakeProjectionEditorProvider.setGUIDTemplate(this.value)" /></div>
								<p class="help">A deterministic key per row so refreshes upsert instead of duplicating. Must reference a field, e.g. <code>{~D:Record.IDProject~}</code>.</p>
							</div>
							<div class="field"><p class="control"><button class="button is-link" onclick="_Pict.providers.DatalakeProjectionEditorProvider.continueFromColumns()">Continue</button></p></div>
						</div>
					</div>

					<!-- Step 3: Name & schedule -->
					<div class="dpe-step">
						<div class="dpe-step-head" onclick="_Pict.providers.DatalakeProjectionEditorProvider.goToStep(3)">
							<span class="dpe-step-num">3</span><span class="dpe-step-title">Name &amp; schedule</span><span class="tag {~D:AppData.DatalakeProjectionEditor.Step3_StatusClass~}">{~D:AppData.DatalakeProjectionEditor.Step3_StatusLabel~}</span>
						</div>
						<div class="dpe-step-body {~D:AppData.DatalakeProjectionEditor.Step3_BodyClass~}">
							<div class="field"><label class="label is-small">Projection name</label>
								<div class="control"><input class="input" type="text" placeholder="MoistureDaily" value="{~D:AppData.DatalakeProjectionEditor.Name~}" onchange="_Pict.providers.DatalakeProjectionEditorProvider.setName(this.value)" /></div>
								{~TS:DPE-NameError:AppData.DatalakeProjectionEditor.NameErrorSlot~}
							</div>
							<div class="field"><label class="label is-small">Target table</label><div class="control"><span class="dpe-target">{~D:AppData.DatalakeProjectionEditor.TargetPreview~}</span></div></div>
							<div class="field"><label class="label is-small">Description (optional)</label><div class="control"><input class="input is-small" type="text" value="{~D:AppData.DatalakeProjectionEditor.Description~}" oninput="_Pict.providers.DatalakeProjectionEditorProvider.setDescription(this.value)" /></div></div>
							<div class="field"><label class="checkbox"><input type="checkbox" {~D:AppData.DatalakeProjectionEditor.ScheduleEnabledAttr~} onchange="_Pict.providers.DatalakeProjectionEditorProvider.setScheduleEnabled(this.checked)" /> Refresh automatically on a schedule</label></div>
							<div class="{~D:AppData.DatalakeProjectionEditor.ScheduleFieldsClass~}">
								<div class="dpe-sched">
									<label class="radio"><input type="radio" name="dpe-sched-mode" {~D:AppData.DatalakeProjectionEditor.ScheduleModeIntervalChecked~} onchange="_Pict.providers.DatalakeProjectionEditorProvider.setScheduleMode('interval')" /> Every N minutes</label>
									<label class="radio"><input type="radio" name="dpe-sched-mode" {~D:AppData.DatalakeProjectionEditor.ScheduleModeCronChecked~} onchange="_Pict.providers.DatalakeProjectionEditorProvider.setScheduleMode('cron')" /> Cron (UTC)</label>
									<div class="field {~D:AppData.DatalakeProjectionEditor.ScheduleIntervalClass~}"><div class="control"><input class="input is-small" type="number" min="1" placeholder="minutes" value="{~D:AppData.DatalakeProjectionEditor.IntervalMinutes~}" oninput="_Pict.providers.DatalakeProjectionEditorProvider.setScheduleField('interval', this.value)" /></div></div>
									<div class="field {~D:AppData.DatalakeProjectionEditor.ScheduleCronClass~}"><div class="control"><input class="input is-small" type="text" placeholder="0 9 * * *" value="{~D:AppData.DatalakeProjectionEditor.Cron~}" oninput="_Pict.providers.DatalakeProjectionEditorProvider.setScheduleField('cron', this.value)" /></div></div>
									<div class="field"><div class="control"><input class="input is-small" type="text" placeholder="pipeline group (optional)" value="{~D:AppData.DatalakeProjectionEditor.Group~}" oninput="_Pict.providers.DatalakeProjectionEditorProvider.setScheduleField('group', this.value)" /></div></div>
								</div>
							</div>
							<div class="field mt-3"><p class="control"><button class="button is-link" onclick="_Pict.providers.DatalakeProjectionEditorProvider.continueFromName()">Review</button></p></div>
						</div>
					</div>

					<!-- Step 4: Review -->
					<div class="dpe-step">
						<div class="dpe-step-head" onclick="_Pict.providers.DatalakeProjectionEditorProvider.goToStep(4)">
							<span class="dpe-step-num">4</span><span class="dpe-step-title">Review</span><span class="tag {~D:AppData.DatalakeProjectionEditor.Step4_StatusClass~}">{~D:AppData.DatalakeProjectionEditor.Step4_StatusLabel~}</span>
						</div>
						<div class="dpe-step-body {~D:AppData.DatalakeProjectionEditor.Step4_BodyClass~}">
							<p class="is-size-7 has-text-grey mb-2">This is the exact request that will be sent to create the projection.</p>
							<pre class="dpe-review">{~D:AppData.DatalakeProjectionEditor.ReviewJSON~}</pre>
							<div class="field is-grouped mt-3"><p class="control"><button class="button is-light" onclick="_Pict.providers.DatalakeProjectionEditorProvider.goToStep(3)">Back</button></p><p class="control"><button class="button is-link" onclick="_Pict.providers.DatalakeProjectionEditorProvider.confirmReview()">Looks good</button></p></div>
						</div>
					</div>

					<!-- Step 5: Create -->
					<div class="dpe-step">
						<div class="dpe-step-head" onclick="_Pict.providers.DatalakeProjectionEditorProvider.goToStep(5)">
							<span class="dpe-step-num">5</span><span class="dpe-step-title">Create</span><span class="tag {~D:AppData.DatalakeProjectionEditor.Step5_StatusClass~}">{~D:AppData.DatalakeProjectionEditor.Step5_StatusLabel~}</span>
						</div>
						<div class="dpe-step-body {~D:AppData.DatalakeProjectionEditor.Step5_BodyClass~}">
							{~TS:DPE-Result:AppData.DatalakeProjectionEditor.ResultSlot~}
							<div class="field is-grouped">
								<p class="control"><button class="button is-link" onclick="_Pict.providers.DatalakeProjectionEditorProvider.submit()"><span class="icon is-small"><i class="fas fa-bolt"></i></span><span>Create projection</span></button>{~TS:DPE-Busy:AppData.DatalakeProjectionEditor.SubmitBusySlot~}</p>
								<p class="control"><button class="button is-light" onclick="_Pict.providers.DatalakeProjectionEditorProvider.viewInCatalog()">View in catalog</button></p>
							</div>
						</div>
					</div>
				</div>`
		},
		{ Hash: 'DPE-EntityOption', Template: /*html*/`<option value="{~D:Record.Entity~}" {~D:Record.SelectedAttr~}>{~D:Record.Label~}</option>` },
		{ Hash: 'DPE-CloneBanner', Template: /*html*/`<div class="notification is-info is-soft py-2 px-3 mb-3"><span class="icon"><i class="fas fa-box-archive"></i></span> <span>Making the imported table <code>{~D:Record.Source~}</code> queryable — a 1:1 copy into a new, registered projection. Name it and create.</span></div>` },
		{
			Hash: 'DPE-EntityPicker',
			Template: /*html*/`
				<div class="field"><label class="label is-small">Source entity (from the Headlight data model)</label>
					<div class="control"><div class="select"><select onchange="_Pict.providers.DatalakeProjectionEditorProvider.setEntity(this.value)"><option value="">— pick an entity —</option>{~TS:DPE-EntityOption:AppData.DatalakeProjectionEditor.SourceEntityOptions~}</select></div></div>
				</div>
				{~TS:DPE-DocTypeField:AppData.DatalakeProjectionEditor.FormDataSlot~}`
		},
		{
			Hash: 'DPE-LakePicker',
			Template: /*html*/`
				<div class="field"><label class="label is-small">Data-lake source</label>
					{~TS:DPE-LakeLoading:AppData.DatalakeProjectionEditor.LakeSourcesLoadingSlot~}
					<div class="control"><div id="DPE-LakeSourcePicker" class="dpe-lake-picker"></div></div>
					{~TS:DPE-LakeEmpty:AppData.DatalakeProjectionEditor.LakeSourcesEmptySlot~}
					<p class="help">Chain off an existing computed projection, or project from an imported clone, into a new lake table.</p>
				</div>`
		},
		{ Hash: 'DPE-LakeGroup', Template: /*html*/`<optgroup label="{~D:Record.GroupLabel~}">{~TS:DPE-LakeOption:Record.Items~}</optgroup>` },
		{ Hash: 'DPE-LakeOption', Template: /*html*/`<option value="{~D:Record.Kind~}::{~D:Record.Physical~}" {~D:Record.SelectedAttr~}>{~D:Record.Label~}</option>` },
		{ Hash: 'DPE-LakeLoading', Template: /*html*/`<p class="help is-size-7 has-text-grey">Loading data-lake sources…</p>` },
		{ Hash: 'DPE-LakeEmpty', Template: /*html*/`<p class="help is-warning">No computed projections or imported clones in your data lake yet.</p>` },
		{ Hash: 'DPE-TestCount', Template: /*html*/`<div class="field is-grouped"><p class="control"><button class="button is-small" onclick="_Pict.providers.DatalakeProjectionEditorProvider.testSource()"><span class="icon is-small"><i class="fas fa-list-ol"></i></span><span>Test source count</span></button></p></div>` },
		{ Hash: 'DPE-TestResult', Template: /*html*/`<div class="notification {~D:Record.Class~} is-soft py-1 px-3 mt-2 is-size-7">{~D:Record.Message~}</div>` },
		{
			Hash: 'DPE-FieldRow',
			Template: /*html*/`
				<tr class="{~D:Record.RowClass~}">
					<td class="has-text-centered"><input type="checkbox" {~D:Record.IncludeAttr~} onchange="_Pict.providers.DatalakeProjectionEditorProvider.toggleField('{~D:Record.SourceField~}')" /></td>
					<td><code>{~D:Record.SourceField~}</code></td>
					<td><input class="input is-small" type="text" value="{~D:Record.OutCol~}" onchange="_Pict.providers.DatalakeProjectionEditorProvider.setOutCol('{~D:Record.SourceField~}', this.value)" /></td>
					<td class="has-text-grey is-size-7">{~D:Record.MeadowType~}</td>
				</tr>`
		},
		{
			Hash: 'DPE-DocTypeField',
			Template: /*html*/`
				<div class="field"><label class="label is-small">Document type</label>
					<div class="control"><input class="input is-small" type="text" placeholder="e.g. WI-Moisture" value="{~D:AppData.DatalakeProjectionEditor.DocumentType~}" onchange="_Pict.providers.DatalakeProjectionEditorProvider.setDocumentType(this.value)" /></div>
					<p class="help">Scopes to documents of this type (sets the filter) and lets you project their form fields in the next step.</p>
				</div>`
		},
		{
			Hash: 'DPE-FormDataSection',
			Template: /*html*/`
				<div class="box mt-3" style="background: var(--bulma-scheme-main-bis, #f6f8fb);">
					<div class="level is-mobile mb-2"><div class="level-left"><strong class="is-size-7">Form fields (FormData)</strong></div><div class="level-right"><button class="button is-small" onclick="_Pict.providers.DatalakeProjectionEditorProvider.addFormDataField()"><span class="icon is-small"><i class="fas fa-plus"></i></span><span>Add form field</span></button></div></div>
					<p class="help mb-2">Project values from each document's form (e.g. <code>FormData.MeterMoisture</code>) — stored as text columns. Enter the form field's key.</p>
					<table class="table is-narrow is-fullwidth"><thead><tr><th>Form field key</th><th>Output column</th><th></th></tr></thead><tbody>{~TS:DPE-FormDataRow:AppData.DatalakeProjectionEditor.FormDataRows~}</tbody></table>
				</div>`
		},
		{
			Hash: 'DPE-FormDataRow',
			Template: /*html*/`
				<tr>
					<td><div class="field has-addons"><p class="control"><a class="button is-small is-static">FormData.</a></p><p class="control is-expanded"><input class="input is-small" type="text" value="{~D:Record.Key~}" onchange="_Pict.providers.DatalakeProjectionEditorProvider.setFormDataKey({~D:Record.Index~}, this.value)" /></p></div></td>
					<td><input class="input is-small" type="text" placeholder="(defaults to the key)" value="{~D:Record.OutCol~}" onchange="_Pict.providers.DatalakeProjectionEditorProvider.setFormDataOutCol({~D:Record.Index~}, this.value)" /></td>
					<td><button class="button is-small is-white" onclick="_Pict.providers.DatalakeProjectionEditorProvider.removeFormDataField({~D:Record.Index~})"><i class="fas fa-trash"></i></button></td>
				</tr>`
		},
		{ Hash: 'DPE-SchemaLoading', Template: /*html*/`<div class="has-text-grey has-text-centered py-3"><span class="dpe-spinner"></span> <span class="ml-2">Loading the entity's columns…</span></div>` },
		{ Hash: 'DPE-SchemaError', Template: /*html*/`<div class="notification is-danger is-soft py-2 px-3 mb-2"><span class="icon"><i class="fas fa-triangle-exclamation"></i></span> <span>{~D:Record.Message~}</span></div>` },
		{ Hash: 'DPE-NameError', Template: /*html*/`<p class="help is-danger">{~D:Record.Message~}</p>` },
		{ Hash: 'DPE-Busy', Template: /*html*/`<span class="dpe-spinner" style="margin-left:0.6rem;"></span>` },
		{
			Hash: 'DPE-Result',
			Template: /*html*/`<div class="notification {~D:Record.Class~} is-soft mb-3"><div class="is-flex"><span class="icon mr-2"><i class="fas {~D:Record.Icon~}"></i></span><div><strong>{~D:Record.Title~}</strong><br><span class="is-size-7">{~D:Record.Detail~}</span></div></div></div>`
		}
	],

	Renderables:
	[
		{
			RenderableHash: 'DatalakeProjectionEditorWrap',
			TemplateHash: 'DPE-Wrap',
			ContentDestinationAddress: '#PlatformConfiguration-Application-Container'
		}
	]
};
